import * as React from 'react'
import axios, { AxiosResponse } from 'axios'
import { useLocale, useTranslation } from 'hooks'
import { encodeUriWith, tracking, envUtils } from 'helpers'
import { ProductInfo } from '../components/product_show/ProductHero/ProductHero.types'
import { useCookieConsentDecision } from './useCookieConsentDecision'

export const useAddToCart = (
  productInfo: ProductInfo,
  selectedSizeConversionId: number,
  selectedPrice: string | null,
  selectedId: number
) => {
  const { t } = useTranslation()
  const { existingCookiePreferences } = useCookieConsentDecision()
  const { localiseUrl, locale } = useLocale()

  // use ref to avoid closure issues when used with <WalletPayment />
  // essentially addToCart and removeFromCart are both passed into the <WalletPayment /> at the same time
  // while cartItemId is `null`.
  const cartItemId = React.useRef(null)

  const selectedSize = productInfo.allSizeConversions.find(
    (sc) => sc.id === selectedSizeConversionId
  )?.international_size_label

  const categoryName = React.useMemo(() => {
    const categories = productInfo.contentCategory.split('>')

    return categories[categories.length - 1].trim()
  }, [productInfo])

  const addToCartRequest = (redirect = true) => {
    const jsonType = 'application/json'
    const afterFunc = (res: AxiosResponse) => {
      if (res.status === 200) {
        const { data } = res

        if (redirect) {
          window.location.href = encodeUriWith(
            'encodeURI',
            localiseUrl(`${window.location.origin}${data.path}`)
          )
        } else {
          cartItemId.current = data.item_id
        }

        return true
      }
      // non-200

      const { data } = res

      if (redirect) {
        window.location.href = encodeUriWith(
          'encodeURI',
          localiseUrl(`${window.location.origin}${data.path}`)
        )
      }

      return false
    }

    return axios({
      url: localiseUrl('/cart', { localeAsParam: true }),
      method: 'POST',
      data: {
        cart: {
          cart_item_id: selectedId
        },
        redirecting: redirect
      },
      headers: {
        'Content-Type': jsonType,
        Accept: jsonType
      }
    })
      .then(afterFunc)
      .catch((res) => afterFunc(res.response))
  }

  const addToCart = async (shouldRedirect = true) => {
    const success = await addToCartRequest(shouldRedirect)

    if (success) {
      tracking.pushToDataLayer('eec.addToCart', {
        ecommerce: {
          currencyCode: productInfo.displayCurrencyCode,
          add: {
            products: [
              {
                name: productInfo.title,
                id: productInfo.styleCode,
                price: selectedPrice,
                brand: productInfo.brand,
                category: categoryName,
                variant: productInfo.colour,
                quantity: 1,
                list: 'Product Page',
                dimension1: selectedPrice,
                dimension2: selectedSize
              }
            ]
          }
        }
      })
    }

    if (
      existingCookiePreferences?.preferences?.analytical &&
      !envUtils.isTestEnv()
    ) {
      tracking.trackAlgoliaAnalytics('convertedObjectIDsAfterSearch', {
        queryID: sessionStorage.getItem('algoliaAnalyticsQueryId'),
        eventName: 'Product Added to Bag',
        index: `Product_${locale}_query_suggestions`,
        objectIDs: [`${productInfo.productId}`]
      })
    }
  }

  return {
    addToCartRequest,
    addToCart,
    cartItemId,
    selectedPrice,
    selectedSize,
    categoryName
  }
}
